// Thin client for the KLIPY GIF API (https://docs.klipy.com).
// The app key is a client-side key (like Stripe's publishable key), exposed via
// the `klipy:app_key` meta tag — set via the `klipy.app_key` encrypted credential,
// with a KLIPY_APP_KEY environment fallback for local development.

import { getMetaContent } from './meta';

const API_ORIGIN = 'https://api.klipy.com';
const PER_PAGE = 24;

export const klipyEnabled = () => Boolean(getMetaContent('klipy:app_key'));

/**
 * Fetch a page of GIFs — trending when `q` is blank, search otherwise.
 * Resolves to { items, hasNext }.
 * @param {{ q?: string, page?: number, customerId?: string|null, signal?: AbortSignal }} options
 */
export const fetchGifs = async ({ q = '', page = 1, customerId = null, signal } = {}) => {
  const appKey = getMetaContent('klipy:app_key');
  if (!appKey) throw new Error('KLIPY app key is not configured');

  const query = q.trim();
  const params = new URLSearchParams({ page: String(page), per_page: String(PER_PAGE) });
  if (query) params.set('q', query);
  if (customerId) params.set('customer_id', customerId);

  const path = query ? 'search' : 'trending';
  const response = await fetch(`${API_ORIGIN}/api/v1/${appKey}/gifs/${path}?${params}`, { signal });
  if (!response.ok) throw new Error(`KLIPY ${path} failed with status ${response.status}`);

  const payload = await response.json();
  if (payload.result !== true) throw new Error(`KLIPY ${path} returned an error result`);

  return {
    items: payload.data?.data || [],
    hasNext: Boolean(payload.data?.has_next),
  };
};

// Format preference for previews in the picker grid: webp is ~5-10x smaller
// than gif at the same size, so prefer it and fall back down the ladder.
const pickFile = (item, sizes, formats) => {
  for (const size of sizes) {
    for (const format of formats) {
      const file = item.file?.[size]?.[format];
      if (file?.url) return file;
    }
  }
  return null;
};

/** Small animated file for grid tiles: { url, width, height } or null. */
export const gifPreviewFile = (item) => pickFile(item, ['sm', 'xs', 'md', 'hd'], ['webp', 'gif']);

/** Full-quality gif for sending: { url, width, height } or null. */
export const gifFullFile = (item) => pickFile(item, ['hd', 'md', 'sm'], ['gif', 'webp']);
